import { defineStore } from 'pinia'
import type {
  IErrorModal,
  IFeatureFlags,
  IFullUserInfo
} from '@/models/interfaces'
import { DateTime } from 'luxon'
import { FEATURE_FLAGS } from '@/constants/featureFlags'

function createEmptyObservedUserInfo(): Partial<IFullUserInfo> {
  return {
    fullName: '',
    coparentFullName: '',
    timeZoneIdentifierString: 'UTC',
    timeZoneNameForMomentJs: 'utc',
    shortDateFormat: 'MM/dd/yyyy'
  }
}

/**
 * Lightweight CommonStore for the observer build.
 *
 * Uses the same Pinia store ID ('common') so that shared composables
 * like useDate can resolve useCommonStore() without pulling in the
 * parent app's heavy CommonStore and its dependency chain
 * (httpClient, WorkerService, IndexedDB workers, etc.).
 *
 * The observer Vite config aliases '@/stores/CommonStore' to this file.
 *
 * All defaults are self-contained here - no $patch from main.observer.ts
 * is needed. Timezone and date-format defaults are placeholders only;
 * main.observer.ts fetches the observed parent's real settings once per
 * session and merges them in via applyFullUserInfo, so timestamps render
 * in the parent's chosen timezone, never the professional's local one.
 */
export const useCommonStore = defineStore('common', {
  state: () => ({
    fullUserInfo: createEmptyObservedUserInfo(),
    windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    // Display-only search state so the shared SearchInput component resolves
    // at setup; observer pages use its v-model path, not this store term
    searchTerm: '',
    errorModal: {
      showErrorModal: false,
      title: '',
      errorMessage: '',
      showFixError: false,
      showGenericError: false
    } as IErrorModal
  }),

  getters: {
    getDateTimeSettings: (state) => ({
      shortDateFormat: state.fullUserInfo.shortDateFormat ?? '',
      timeZoneNameForMomentJs: state.fullUserInfo.timeZoneNameForMomentJs ?? ''
    }),

    isDesktopWidth: (state) => state.windowWidth >= 1025,
    isTabletWidth: (state) =>
      state.windowWidth >= 768 && state.windowWidth < 1025,
    isMobileWidth: (state) => state.windowWidth < 768,

    // No Remote Config, no tag-gating, no other observer flags today -
    // web_spanish_version is the only flag the observer checks (via the
    // shared i18nHelpers.isSpanishFlagEnabled), and it's a fixed boolean:
    // always on outside production, off in production.
    isProd: () => import.meta.env.VITE_NODE_ENV === 'production'
  },

  actions: {
    setWindowWidthChanges() {
      this.windowWidth = window.innerWidth
    },

    setSearchTerm(value: string) {
      this.searchTerm = value
    },

    /**
     * Sync the observed client identity from the professional session so
     * shared components (e.g. TranscriptItem) that compare against
     * fullUserInfo.userId / fullName / coparentFullName resolve speakers
     * exactly like the consumer app does.
     */
    setObservedIdentity(identity: {
      userId: number | null
      fullName: string
      coparentFullName: string
    }) {
      if (this.fullUserInfo.userId !== identity.userId) {
        this.fullUserInfo = createEmptyObservedUserInfo()
      }
      this.fullUserInfo = {
        ...this.fullUserInfo,
        ...(identity.userId !== null ? { userId: identity.userId } : {}),
        fullName: identity.fullName,
        coparentFullName: identity.coparentFullName
      }
    },

    resetObservedClientState() {
      this.fullUserInfo = createEmptyObservedUserInfo()
    },

    /**
     * Merge the observed parent's real account info (timezone, date format)
     * fetched from the API. Identity fields already synced from the
     * professional session (userId, fullName, coparentFullName) stay
     * authoritative - the merge never clobbers a populated value.
     */
    applyFullUserInfo(info: Partial<IFullUserInfo>) {
      const { userId, fullName, coparentFullName } = this.fullUserInfo
      this.fullUserInfo = {
        ...this.fullUserInfo,
        ...info,
        ...(userId ? { userId } : {}),
        ...(fullName ? { fullName } : {}),
        ...(coparentFullName ? { coparentFullName } : {})
      }
    },

    setErrorModal(value: IErrorModal) {
      this.errorModal = value
    },

    resetErrorModal() {
      this.errorModal = {
        showErrorModal: false,
        title: '',
        errorMessage: '',
        showFixError: false,
        showGenericError: false
      }
    },

    isFeatureEnabled(flag: keyof IFeatureFlags): boolean {
      return flag === FEATURE_FLAGS.SPANISH_VERSION && !this.isProd
    },

    formatDateInUserTz(
      date: DateTime | string | null | undefined,
      overrideFormat?: string
    ): string {
      if (!date) return ''
      const tz = this.fullUserInfo.timeZoneNameForMomentJs ?? 'utc'
      const fmt =
        overrideFormat ?? this.fullUserInfo.shortDateFormat ?? 'MM/dd/yyyy'

      if (date instanceof DateTime) {
        return date.setZone(tz).toFormat(fmt)
      }

      return DateTime.fromISO(date, { zone: 'utc' }).setZone(tz).toFormat(fmt)
    }
  }
})
